import * as React from "react";
import { MapPinned, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCitiesSearchQuery } from "./useCities";
import { AsyncSelect } from "@shared/components/AsyncSelect";
import { Badge } from "@shared/components/ui/badge";

export function SelectCity({
  city,
  onSelectCity,
  className,
  showCountry = true,
  showAllOption = false, // New prop to show "All" option
}) {
  const { t } = useTranslation();
  const [value, setValue] = React.useState(city || "");

  const handleValueChange = (newValue) => {
    setValue(newValue);
  };

  const handleItemSelect = (selectedCity) => {
    // Handle "All" option - when selectedCity is null, pass an object with empty name
    if (!selectedCity) {
      onSelectCity({ name: "" });
    } else {
      onSelectCity(selectedCity);
    }
  };

  // Enhanced label formatting with country info and styling
  const formatCityLabel = (city) => {
    if (showCountry) {
      return (
        <div className="flex items-center justify-between w-full py-1">
          <div className="flex flex-col flex-1 min-w-0">
            <span className="font-medium text-foreground truncate">
              {city.name}
            </span>
            {(city.country || city.nameArabic) && (
              <div className="flex items-center space-x-2 text-xs text-muted-foreground mt-0.5">
                {city.country && (
                  <span className="flex items-center">
                    <Globe className="h-3 w-3 mr-1" />
                    {city.country}
                  </span>
                )}
                {city.nameArabic && (
                  <span className="text-right" dir="rtl">
                    {city.nameArabic}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }
    return `${city.name}${city.nameArabic ? ` | ${city.nameArabic}` : ""}`;
  };

  // Enhanced selected label format
  const formatSelectedLabel = (city) => {
    return (
      <div className="flex items-center space-x-2">
        <span className="font-medium mx-2">{city.name}</span>
        {city.nameArabic && (
          <Badge variant="outline" className="text-xs">
            {city.nameArabic}
          </Badge>
        )}
      </div>
    );
  };

  return (
    <AsyncSelect
      value={value}
      onValueChange={handleValueChange}
      onItemSelect={handleItemSelect}
      placeholder={t("action.select", {
        entity: t("user.city"),
      })}
      searchPlaceholder={t("action.select", {
        entity: t("user.city"),
      })}
      notFoundMessage={t("main.notFound", {
        entity: t("user.city"),
      })}
      icon={<MapPinned className="h-4 w-4 text-primary flex-shrink-0 rtl:ml-2 ltr:mr-2" />}
      useSearchQuery={useCitiesSearchQuery}
      extractItems={(response) => response?.cities || []}
      getItemKey={(city) => city._id}
      getItemValue={(city) => city.name}
      getItemLabel={formatCityLabel}
      formatSelectedLabel={formatSelectedLabel}
      debounceDelay={300}
      searchLimit={1000}
      className={className}
      showAllOption={showAllOption}
      allOptionLabel={t("main.all")}
      allOptionValue=""
    />
  );
}
